import { JobType } from '../../../api/jobs.dao';
import { JobExecutionRunner } from './job-execution-runner';
/* Used for the common Job triggered and executed in different app states */
const jobTypeCompletionHandlersRegistry: Map<JobType, (() => void)[]> = new Map();
/* Used for the concrete Job triggered and executed in the same app state (no app reload) */
const jobIdCompletionHandlersRegistry: Map<number, (() => void)> = new Map();

const handlerGuard = (handler: (() => void)) => {
    if (typeof handler !== 'function') throw new Error('provided "handler" is not a Function');
};

const idGuard = (id: number) => {
    if (typeof id !== 'number') throw new Error('provided "id" is not a Number');
};

/**
 * Stored callback will be associated with job id after next invocation.
 */
export function registerJobCompletionHandler(handler: () => void): (id: number) => void {
    handlerGuard(handler);
    return function registerHandlerById(id: number): void {
        idGuard(id);
        jobIdCompletionHandlersRegistry.set(id, handler);
    };
}


export function registerJobCompletionHandlerById(id: number, handler: () => void): void {
    idGuard(id);
    handlerGuard(handler);
    jobIdCompletionHandlersRegistry.set(id, handler);
}

export async function runRegisteredHandlerById(id: number): Promise<void> {
    if (!jobIdCompletionHandlersRegistry.has(id)) return;

    try {
        await jobIdCompletionHandlersRegistry.get(id)();
    } catch (e) {
        throw new Error(`Registered handler with "id ${id}" - failed to execute`);
    } finally {
        jobIdCompletionHandlersRegistry.delete(id);
    }
}

export function registerJobCompletionHandlerByType(type: JobType, handler): void {
    if (!jobTypeCompletionHandlersRegistry.has(type)) jobTypeCompletionHandlersRegistry.set(type, []);

    jobTypeCompletionHandlersRegistry.set(type, jobTypeCompletionHandlersRegistry.get(type).concat(handler));
}

export async function runRegisteredHandlersByType(type): Promise<void> {
    if (!jobTypeCompletionHandlersRegistry.has(type)) return;

    jobTypeCompletionHandlersRegistry.get(type).forEach(Reflect.apply);
}

export function runRegisteredHandlers(executionRunner: JobExecutionRunner): Promise<void> {
    if (jobIdCompletionHandlersRegistry.has(executionRunner.id)) return runRegisteredHandlerById(executionRunner.id);
    if (jobTypeCompletionHandlersRegistry.has(executionRunner.type)) return runRegisteredHandlersByType(executionRunner.type);
}

export function reset() {
    jobIdCompletionHandlersRegistry.clear();
    jobTypeCompletionHandlersRegistry.clear();
}
